import * as Dfns from 'date-fns/fp';
import { useCallback } from 'react';
import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { MedianOrMean } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { MmmFeatureNameResponse } from '@blisspointmedia/bpm-types/dist/MMMFeatureNames';
import { awaitJSON, HROASPyLambdaFetch } from '@src/utils/fetch-utils';
import { isRawFeatureId } from '@src/utils/mmm-feature-mapping';
import type { DecayData, RoasData } from '@blisspointmedia/bpm-types/dist/MMM';
import { ModelConfig } from '../hroas-types';
import {
  DecayStructure,
  HolisticRcCsvRow,
  parseChannelColumn,
  SpendEffectShareCsvRow,
  StackedCSVRow,
  transformCsvToDecayDataArray,
  transformHolisticDecayCsvToDecayData
} from './hroasUtils';
import {
  datasetRows,
  fetchHroasResults,
  getHroasResultsQueryKey,
  HroasResultsBundle,
  latestSpendEffectShareKey,
  runScopedSpendEffectShareKey
} from './hroasResults';
import {
  decayKeyOf,
  decaySlotIdByKey,
  deriveSlots,
  familySlotIds,
  Slot,
  SlotRow,
  tagRowsWithSlot
} from './slots';
import { isHiddenChannel } from './tinuitiLiveFilters';
import { FeatureMapsByModelType, toFeatureMappingType } from './useFeatureMapping';
import {
  resolveFeatureIdToName,
  resolveFeatureRecordMultiMap,
  resolveFeatureRowMultiMap,
  resolveRawFeatureName
} from './featureIdResolution';

const HROAS_QUERY_KEY_PREFIX = 'hroas';

/**
 * Shared fetch for SES/RC/MO/MDD — the four hooks share one query key. Keyed on
 * the derived kpi + the selected run so the panels read `runs/{runId}/` instead
 * of `latest/`; a null runId is only ever fetched behind an `enabled: !!runId`
 * gate (never as a real request).
 */
const fetchModelResultsBundle = (
  company: string,
  kpi: string,
  runId: string | null
): Promise<HroasResultsBundle> => fetchHroasResults(company, kpi, 'model_results', runId);

const MODEL_RESULTS_INCLUDE = 'model_results' as const;

export interface ActualizedCsvRow {
  model_type: string;
  model_kpi?: string;
  metric: string;
  media_channel?: string;
  channel?: string;
  time?: string;
  date?: string;
  actualized_spend?: number | null;
  actualized_effect?: number | null;
  media_type?: 'demand_generation' | 'demand_capture';
  refresh_date?: string;
  // DEV-24605: TTM dark-channel flag (see SpendEffectShareCsvRow.dark_last_year).
  dark_last_year?: number;
}

/** Shared react-query key for the model-results bundle (SES / RC / MO / MDD). */
export const getSpendEffectShareCsvQueryKey = (
  company: string,
  kpi: string,
  runId: string | null
): readonly unknown[] => getHroasResultsQueryKey(company, kpi, MODEL_RESULTS_INCLUDE, runId);

/**
 * Full SES rows (raw + actualized); the actualized subset is date-joined downstream. New-format
 * rows (blank name + feature_id) are resolved to old-format-shaped rows via the per-model-type
 * feature maps BEFORE the hidden-channel filter so isHiddenChannel and every downstream parser see
 * a populated channel. Each row resolves against the map for ITS OWN model_type (HROAS blends
 * rMMM/GeoMMM/BrandEquity rows in one bundle), not one global map.
 */
export const selectSpendEffectShareRows = (
  bundle: HroasResultsBundle,
  featureMaps: FeatureMapsByModelType | undefined
): SpendEffectShareCsvRow[] =>
  tagRowsWithSlot(
    (
      datasetRows(bundle.data.spendEffectShare) as unknown as Omit<
        SpendEffectShareCsvRow,
        'slotId'
      >[]
    )
      .map(row =>
        resolveFeatureRowMultiMap(row, r => toFeatureMappingType(r.model_type), featureMaps)
      )
      .filter(row => !isHiddenChannel(row.channel)),
    deriveSlots(bundle.models)
  );

export const useSpendEffectShareCsvData = (
  company: string,
  kpi: string,
  runId: string | null,
  featureMaps?: FeatureMapsByModelType
): UseQueryResult<SpendEffectShareCsvRow[], unknown> => {
  const select = useCallback(
    (bundle: HroasResultsBundle) => selectSpendEffectShareRows(bundle, featureMaps),
    [featureMaps]
  );
  return useQuery({
    queryKey: getSpendEffectShareCsvQueryKey(company, kpi, runId),
    queryFn: () => fetchModelResultsBundle(company, kpi, runId),
    select,
    enabled: Boolean(company) && Boolean(runId),
    staleTime: 0,
    refetchOnMount: 'always',
    // EfficientFrontier.tsx already surfaces this query's error via useSetError — suppress the
    // global QueryCache fallback (src/main.tsx) so it doesn't double-show the dialog.
    meta: { suppressGlobalError: true }
  });
};

export const adaptSpendEffectShareRowsToActualizedCsvRows = (
  rows: SpendEffectShareCsvRow[]
): ActualizedCsvRow[] =>
  rows
    .filter(r => r.attribution_type === 'actualized')
    .map(r => ({
      model_type: r.slotId,
      model_kpi: r.kpi,
      metric: r.estimate_type,
      media_channel: undefined,
      channel: r.name,
      date: r.date,
      actualized_spend: r.spend,
      actualized_effect: r.contribution,
      media_type: r.media_type as ActualizedCsvRow['media_type'],
      refresh_date: undefined,
      dark_last_year: Number(r.dark_last_year ?? 0)
    }));

/** Shared react-query key for the model-results bundle (same key as SES). */
export const getHolisticRcQueryKey = (
  company: string,
  kpi: string,
  runId: string | null
): readonly unknown[] => getHroasResultsQueryKey(company, kpi, MODEL_RESULTS_INCLUDE, runId);

/**
 * Normalize the response_curves rows into the legacy HolisticRcCsvRow shape.
 * The CSV has 2 rows per data point — attribution_type="raw" and "actualized" —
 * which we fold into one row with the raw and actualized columns side by side.
 * Also drops hidden (Podcast/Super Bowl) channels.
 */
const normalizeResponseCurveRows = (
  rawInputRows: Record<string, unknown>[],
  featureMaps: FeatureMapsByModelType | undefined,
  slots: Slot[]
): HolisticRcCsvRow[] => {
  // Resolve new-format rows (blank name + feature_id) up front so `base.name` — which becomes the
  // row's `media_channel` below — is a populated, comma-delimited name the downstream parsers read.
  // Each row resolves against the map for ITS OWN model_type (HROAS blends rMMM/GeoMMM/BrandEquity
  // rows in one bundle), not one global map.
  const rawRows: Record<string, unknown>[] = tagRowsWithSlot(
    rawInputRows.map(row =>
      resolveFeatureRecordMultiMap(
        row,
        r => toFeatureMappingType(r.model_type as string | undefined),
        featureMaps
      )
    ) as (Record<string, unknown> & SlotRow)[],
    slots
  ).map(row => ({ ...row, model_type: row.slotId }));
  const allData: HolisticRcCsvRow[] = (() => {
    if (rawRows.length === 0 || rawRows[0].attribution_type === undefined) {
      return rawRows as unknown as HolisticRcCsvRow[];
    }

    const pairMap = new Map<
      string,
      { raw?: Record<string, unknown>; actualized?: Record<string, unknown> }
    >();

    rawRows.forEach(row => {
      const key = `${row.name}__${row.estimate_type}__${row.curve_point}__${row.model_type}`;
      if (!pairMap.has(key)) {
        pairMap.set(key, {});
      }
      const pair = pairMap.get(key)!;
      if (row.attribution_type === 'raw') {
        pair.raw = row;
      } else if (row.attribution_type === 'actualized') {
        pair.actualized = row;
      }
    });

    const normalized: HolisticRcCsvRow[] = [];
    pairMap.forEach(({ raw, actualized }) => {
      const base = raw ?? actualized;
      if (!base) {
        return;
      }
      normalized.push({
        kpi: String(base.kpi ?? ''),
        model_type: String(base.model_type ?? ''),
        media_channel: String(base.name ?? ''),
        credible_interval: 0.8,
        weights: 1,
        estimate_type: String(base.estimate_type ?? ''),
        raw_spend: Number(raw?.spend ?? 0),
        actualized_spend: Number(actualized?.spend ?? 0),
        raw_effect: Number(raw?.response ?? 0),
        actualized_effect: Number(actualized?.response ?? 0),
        raw_return_next_dollar: Number(raw?.return_next_dollar ?? 0),
        actualized_return_next_dollar: Number(actualized?.return_next_dollar ?? 0),
        previous_qtr_mean_value: Number(base.previous_qtr_average_value ?? 0),
        dark_last_quarter: Number(base.dark_last_quarter ?? 0)
      });
    });

    return normalized;
  })();

  return allData.filter(row => {
    const mediaChannel = row.media_channel;
    if (!mediaChannel) {
      return true;
    }
    const isNewFormat = !mediaChannel.includes('__');
    const { channel } = parseChannelColumn(isNewFormat ? mediaChannel : undefined, mediaChannel);
    return !isHiddenChannel(channel);
  });
};

export const selectHolisticRcRows = (
  bundle: HroasResultsBundle,
  featureMaps: FeatureMapsByModelType | undefined
): HolisticRcCsvRow[] =>
  normalizeResponseCurveRows(
    datasetRows(bundle.data.responseCurves),
    featureMaps,
    deriveSlots(bundle.models)
  );

export const useHolisticRcData = (
  company: string,
  kpi: string,
  runId: string | null,
  featureMaps?: FeatureMapsByModelType
): UseQueryResult<HolisticRcCsvRow[], unknown> => {
  const select = useCallback(
    (bundle: HroasResultsBundle) => selectHolisticRcRows(bundle, featureMaps),
    [featureMaps]
  );
  return useQuery({
    queryKey: getHolisticRcQueryKey(company, kpi, runId),
    queryFn: () => fetchModelResultsBundle(company, kpi, runId),
    select,
    enabled: Boolean(company) && Boolean(runId),
    staleTime: 0,
    refetchOnMount: 'always'
  });
};

const isoToMddyyyy = (isoDate: string): string => {
  try {
    const parsed = Dfns.parseISO(isoDate);
    return Dfns.isValid(parsed) ? Dfns.format('M/d/yyyy', parsed) : isoDate;
  } catch {
    return isoDate;
  }
};

/** Map model_overview.csv rows (geo × date prediction/actual/baseline/...) to slot-tagged StackedCSVRow. */
const mapModelOverviewRows = (rows: Record<string, unknown>[], slots: Slot[]): StackedCSVRow[] =>
  tagRowsWithSlot(
    rows.map(row => ({
      refresh_date: isoToMddyyyy(String(row.refresh_date ?? '')),
      model_type: String(row.model_type),
      model_scope: row.model_scope ? String(row.model_scope) : undefined,
      model_kpi: String(row.model_kpi ?? ''),
      date: isoToMddyyyy(String(row.date ?? '')),
      geo: String(row.geo ?? ''),
      is_test: String(row.is_test ?? ''),
      metric: 'mean',
      predicted: Number(row.predicted),
      actual: Number(row.actual),
      baseline: Number(row.baseline),
      paid_attribution: Number(row.paid_attribution),
      media_spend: Number(row.media_spend)
    })),
    slots
  ).map(({ model_scope: _scope, slotId, ...row }) => ({ ...row, model_type: slotId }));

export const selectModelOverviewRows = (bundle: HroasResultsBundle): StackedCSVRow[] =>
  mapModelOverviewRows(datasetRows(bundle.data.modelOverview), deriveSlots(bundle.models));

/** Direct (non-hook) Model Overview fetch for HROAS.tsx's initial stacked-CSV load. */
export const fetchModelOverviewRows = async (
  company: string,
  kpi: string,
  runId: string | null
): Promise<StackedCSVRow[]> =>
  selectModelOverviewRows(await fetchModelResultsBundle(company, kpi, runId));

export const getModelOverviewLambdaQueryKey = (
  company: string,
  kpi: string,
  runId: string | null
): readonly unknown[] => getHroasResultsQueryKey(company, kpi, MODEL_RESULTS_INCLUDE, runId);

export const useModelOverviewLambdaData = (
  company: string,
  kpi: string,
  runId: string | null
): UseQueryResult<StackedCSVRow[], unknown> =>
  useQuery({
    queryKey: getModelOverviewLambdaQueryKey(company, kpi, runId),
    queryFn: () => fetchModelResultsBundle(company, kpi, runId),
    select: selectModelOverviewRows,
    enabled: Boolean(company) && Boolean(runId),
    staleTime: 0,
    refetchOnMount: 'always'
  });

/**
 * The run's trained model slots (inputs.json models[]) for the model-toggle list and every
 * slot-aware adapter. Shares the model-results bundle key (SES/RC/MO/MDD + prefetch), so this
 * reads from the same run-scoped cache entry with no extra request and just applies its own
 * `select`.
 */
export const useRunSlots = (
  company: string,
  kpi: string,
  runId: string | null
): UseQueryResult<Slot[], unknown> => {
  const select = useCallback(
    (bundle: HroasResultsBundle): Slot[] => deriveSlots(bundle.models),
    []
  );
  return useQuery({
    queryKey: getModelOverviewLambdaQueryKey(company, kpi, runId),
    queryFn: () => fetchModelResultsBundle(company, kpi, runId),
    select,
    enabled: Boolean(company) && Boolean(runId),
    staleTime: 0,
    refetchOnMount: 'always'
  });
};

export interface MddRow {
  kpi: string;
  model_type: string;
  slotId: string;
  feature_id: string;
  // Feature-id output format (client `away`): resolved via the getMmmFeatureNames map.
  model_scope?: string;
  regional_scope?: string;
  name: string;
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
  media_type: string;
  dark_last_quarter: string;
  incrementality_test: string;
  geo: string;
  attribution_type: string;
  estimate_type: string;
  previous_qtr_average_spend: string;
  response_to_previous_qtr_average_spend: string;
  all_time_roas: string;
  previous_qtr_roas: string;
  all_time_cpa: string;
  previous_qtr_cpa: string;
  next_dollar_effectiveness: string;
  spend_needed_for_one_more_kpi: string;
}

export const adaptMddRowsToRoasData = (
  rows: MddRow[],
  enabledModelTypes: string[],
  normalizedWeights: Map<string, number>,
  centralMeasure: MedianOrMean
): RoasData[] => {
  const filtered = rows.filter(
    r =>
      r.attribution_type === 'actualized' &&
      r.estimate_type === 'mean' &&
      enabledModelTypes.includes(r.slotId)
  );

  const groups = new Map<string, MddRow[]>();
  filtered.forEach(r => {
    const list = groups.get(r.name) ?? [];
    list.push(r);
    groups.set(r.name, list);
  });

  const result: RoasData[] = [];

  groups.forEach(groupRows => {
    const first = groupRows[0];

    let maxSpend = 0;
    let totalWeightedResponse = 0;
    let totalWeightedNextDollar = 0;
    let sumAllTimeRoas = 0;
    let sumAllTimeCpa = 0;
    let sumPrevQtrRoas = 0;
    let sumPrevQtrCpa = 0;

    groupRows.forEach(r => {
      const w = normalizedWeights.get(r.slotId) ?? 0;
      maxSpend = Math.max(maxSpend, Number(r.previous_qtr_average_spend) || 0);
      totalWeightedResponse += Number(r.response_to_previous_qtr_average_spend) * w;
      totalWeightedNextDollar += Number(r.next_dollar_effectiveness) * w;
      sumAllTimeRoas += Number(r.all_time_roas);
      sumAllTimeCpa += Number(r.all_time_cpa);
      sumPrevQtrRoas += Number(r.previous_qtr_roas);
      sumPrevQtrCpa += Number(r.previous_qtr_cpa);
    });

    const prevQtrRoas = maxSpend > 0 ? sumPrevQtrRoas : undefined;
    const prevQtrCpa = maxSpend > 0 ? sumPrevQtrCpa : undefined;
    const allTimeRoas = groupRows.length > 0 ? sumAllTimeRoas : undefined;
    const allTimeCpa = groupRows.length > 0 ? sumAllTimeCpa : undefined;

    const isMedian = centralMeasure === MedianOrMean.MEDIAN;

    const row: Partial<RoasData> = {
      name: first.name,
      channel: first.channel || undefined,
      platform: first.platform || undefined,
      tactic: first.tactic || undefined,
      brand_nonbrand: first.brand_nonbrand || undefined,
      dark_last_quarter: Number(first.dark_last_quarter),
      incrementality_test: Boolean(first.incrementality_test),
      next_dollar_effectiveness: totalWeightedNextDollar,
      all_time_roas: allTimeRoas,
      previous_qtr_roas: prevQtrRoas,
      all_time_cpa: allTimeCpa,
      previous_qtr_cpa: prevQtrCpa,
      response_previous_qtr_median_spend:
        maxSpend > 0 ? totalWeightedResponse || undefined : undefined,
      response_previous_qtr_mean_spend:
        maxSpend > 0 ? totalWeightedResponse || undefined : undefined
    };

    if (isMedian) {
      row.previous_qtr_median_spend = maxSpend || undefined;
      row.median = maxSpend > 0 ? 1 : 0;
      row.mean = 0;
    } else {
      row.previous_qtr_mean_spend = maxSpend || undefined;
      row.mean = maxSpend > 0 ? 1 : 0;
      row.median = 0;
    }

    result.push(row as RoasData);
  });

  return result;
};

export const selectMddRows = (
  bundle: HroasResultsBundle,
  featureMaps: FeatureMapsByModelType | undefined
): MddRow[] =>
  tagRowsWithSlot(
    (datasetRows(bundle.data.marginalDecisionData) as unknown as Omit<MddRow, 'slotId'>[])
      .map(row =>
        resolveFeatureRowMultiMap(row, r => toFeatureMappingType(r.model_type), featureMaps)
      )
      .filter(row => !isHiddenChannel(row.channel)),
    deriveSlots(bundle.models)
  );

export const getMarginalDecisionQueryKey = (
  company: string,
  kpi: string,
  runId: string | null
): readonly unknown[] => getHroasResultsQueryKey(company, kpi, MODEL_RESULTS_INCLUDE, runId);

export const useMarginalDecisionLambdaData = (
  company: string,
  kpi: string,
  runId: string | null,
  featureMaps?: FeatureMapsByModelType
): UseQueryResult<MddRow[], unknown> => {
  const select = useCallback(
    (bundle: HroasResultsBundle) => selectMddRows(bundle, featureMaps),
    [featureMaps]
  );
  return useQuery({
    queryKey: getMarginalDecisionQueryKey(company, kpi, runId),
    queryFn: () => fetchModelResultsBundle(company, kpi, runId),
    select,
    enabled: Boolean(company) && Boolean(runId),
    staleTime: 0,
    refetchOnMount: 'always'
  });
};

export interface DecayCurvesLambdaRow {
  client: string;
  model: string;
  period: number;
  name: string;
  // getDecayCurves emits a dedicated feature_id column (added in #186). For new-format clients
  // (e.g. away) the `name` is blank and the real channel id lives here; carry it through so the
  // decay/saturation labels can resolve off it (old clients omit it -> undefined).
  feature_id?: string;
  spend_carryover: number;
  max_lag: number;
  dark_last_quarter: number;
}

interface DecayCurvesLambdaResponse {
  decayCurves: {
    columns: { accessorKey: string }[];
    rows: DecayCurvesLambdaRow[];
    totalCount: number;
  };
}

interface DecayCurvesBody {
  modelS3Bucket: string;
  rawDecayCsvS3Key: string;
  spendEffectShareCsvS3Key: string;
  client: string;
  models: Record<string, { type: string; userWeight: number; scope?: string }>;
}

// Resolve the spend_effect_share.csv S3 key the getDecayCurves lambda should read. The SES is the
// same file getHroasResults serves — always at the run prefix `hroas/{company}/{kpi}/runs/{runId}/`
// (results.py `_runPrefix`; the MDD batch path also derives SES from the run dir), or the `latest/`
// mirror when no run is selected. Runs co-locate raw_decay_data with SES in that run dir, so
// swapping the filename off rawDecayCsvS3Key already lands on the run-scoped SES.
const resolveSpendEffectShareKey = (
  company: string,
  kpi: string,
  runId: string | null,
  rawDecayCsvS3Key: string
): string => {
  const swapped = rawDecayCsvS3Key.replace(/raw_decay_data\.csv$/, 'spend_effect_share.csv');
  // The filename swap silently no-ops if rawDecayCsvS3Key ever has an unexpected shape, which
  // would send the raw-decay key as the SES key (a 200 with the wrong file). Only trust the swap
  // when it actually changed the string; otherwise fall back to the run-scoped / latest key.
  if (swapped !== rawDecayCsvS3Key) {
    return swapped;
  }
  if (runId) {
    return runScopedSpendEffectShareKey(company, kpi, runId);
  }
  return latestSpendEffectShareKey(company, kpi);
};

export const buildDecayCurvesBody = (
  company: string,
  kpi: string,
  runId: string | null,
  rawDecayCsvS3Key: string,
  models: ModelConfig[],
  slots: Slot[]
): DecayCurvesBody => {
  const modelsField: DecayCurvesBody['models'] = {};
  models
    .filter(model => model.enabled)
    .forEach(model => {
      const slot = slots.find(candidate => candidate.slotId === model.id);
      const key = slot && decayKeyOf(slots, slot);
      if (!slot || !key) {
        return;
      }
      modelsField[key] = {
        type: slot.family,
        userWeight: model.weight,
        ...(familySlotIds(slots, slot.family).length > 1 && { scope: slot.modelScope })
      };
    });
  return {
    modelS3Bucket: 'tinuiti-mmm-results',
    rawDecayCsvS3Key,
    spendEffectShareCsvS3Key: resolveSpendEffectShareKey(company, kpi, runId, rawDecayCsvS3Key),
    client: company.toLowerCase(),
    models: modelsField
  };
};

export const buildDecayData = (
  allRows: DecayCurvesLambdaRow[],
  slots: Slot[],
  featureMap?: MmmFeatureNameResponse
): { decayData: DecayData[]; decayStructure: DecayStructure | null } => {
  const slotIdByDecayKey = decaySlotIdByKey(slots);
  const rawRows = allRows.filter(
    row => row.model === 'holistic' || Object.hasOwn(slotIdByDecayKey, row.model)
  );
  // Resolve the decay/saturation channel label. getDecayCurves emits both a `name` and (since #186)
  // a dedicated `feature_id` column:
  //   - New-format clients (e.g. away): `name` is blank/raw and the real id is in `feature_id` ->
  //     resolve off the feature_id column to the comma-joined reconstructed name.
  //   - Old clients: `feature_id` is absent (or `name` is already human-readable) -> keep today's
  //     behavior exactly (resolveRawFeatureName fixes a raw id embedded in `name`, else passthrough).
  // Gate the feature_id-column path on feature_id being present AND `name` blank or a raw id, so
  // old-client rows are never re-routed.
  const rows = rawRows.map(row => {
    const featureId = row.feature_id;
    const rawName = row.name == null ? '' : String(row.name);
    const useFeatureIdColumn =
      featureId != null &&
      String(featureId).trim().length > 0 &&
      (rawName.trim().length === 0 || isRawFeatureId(rawName));
    const name = useFeatureIdColumn
      ? resolveFeatureIdToName(featureId, featureMap)
      : resolveRawFeatureName(rawName, featureMap);
    return { ...row, name };
  });
  const structure = transformHolisticDecayCsvToDecayData(rows, slotIdByDecayKey);
  const decayDataArray = transformCsvToDecayDataArray(rows, slotIdByDecayKey) as DecayData[];
  // Tinuiti Live: drop hidden channels from both representations.
  const filteredDecayData = decayDataArray.filter(row => !isHiddenChannel(row.channel));
  const filteredStructure: DecayStructure = structure
    ? Object.fromEntries(Object.entries(structure).filter(([channel]) => !isHiddenChannel(channel)))
    : structure;
  return { decayData: filteredDecayData, decayStructure: filteredStructure };
};

export const fetchDecayCurvesFromLambda = async (
  body: DecayCurvesBody,
  slots: Slot[],
  featureMap?: MmmFeatureNameResponse
): Promise<{ decayData: DecayData[]; decayStructure: DecayStructure | null }> => {
  const res = await HROASPyLambdaFetch('/getDecayCurves', { method: 'POST', body });
  const data = await awaitJSON<DecayCurvesLambdaResponse>(res);
  return buildDecayData(data.decayCurves?.rows ?? [], slots, featureMap);
};

export const getDecayCurvesLambdaQueryKey = (
  company: string,
  models: ModelConfig[]
): readonly unknown[] => {
  const enabledModels = models.filter(m => m.enabled);
  const totalWeight = enabledModels.reduce((sum, m) => sum + m.weight, 0);
  const weightEntries = enabledModels
    .map(
      m => `${m.id}:${totalWeight > 0 ? Math.round((m.weight / totalWeight) * 10000) / 10000 : 0}`
    )
    .sort();
  return [HROAS_QUERY_KEY_PREFIX, 'decayCurvesLambda', company, ...weightEntries];
};

export const isDecayPreloadSettled = (
  runInputsResolved: boolean,
  hasRawDecayKey: boolean,
  decayQuerySucceeded: boolean
): boolean => {
  if (!runInputsResolved) {
    return false;
  }
  if (!hasRawDecayKey) {
    return true;
  }
  return decayQuerySucceeded;
};

interface RunInputs {
  resolved: boolean;
  rawDecayKey: string | null;
  slots: Slot[];
}

const NO_SLOTS: Slot[] = [];

export const useRunInputs = (company: string, kpi: string, runId: string | null): RunInputs => {
  const { isSuccess, data } = useQuery({
    queryKey: getHroasResultsQueryKey(company, kpi, MODEL_RESULTS_INCLUDE, runId),
    queryFn: () => fetchModelResultsBundle(company, kpi, runId),
    select: (bundle: HroasResultsBundle) => ({
      rawDecayKey: bundle.econInputs?.raw_decay_data ?? null,
      slots: deriveSlots(bundle.models)
    }),
    enabled: Boolean(company) && Boolean(runId),
    staleTime: 0,
    refetchOnMount: 'always'
  });
  return {
    resolved: isSuccess,
    rawDecayKey: data?.rawDecayKey ?? null,
    slots: data?.slots ?? NO_SLOTS
  };
};

export const useDecayCurvesLambdaData = (
  company: string,
  models: ModelConfig[],
  kpi: string,
  runId: string | null,
  featureMap?: MmmFeatureNameResponse
): UseQueryResult<{ decayData: DecayData[]; decayStructure: DecayStructure | null }, unknown> => {
  const enabledCount = models.filter(m => m.enabled).length;
  // Refetch once when the feature map transitions absent -> present, so decay labels re-resolve if
  // the map arrives after the decay POST already ran (the map is warmed at the page level).
  const featureMapReady = featureMap ? Object.keys(featureMap.featureIdMap).length : 0;

  // Shares the model-results bundle key with the other panels, so this reads
  // from the same run-scoped cache entry and re-fetches when run/kpi change.
  const { rawDecayKey, slots } = useRunInputs(company, kpi, runId);

  return useQuery({
    queryKey: [
      ...getDecayCurvesLambdaQueryKey(company, models),
      kpi,
      runId,
      rawDecayKey,
      featureMapReady
    ],
    queryFn: () => {
      if (rawDecayKey == null) {
        return Promise.resolve({ decayData: [], decayStructure: null });
      }
      const body = buildDecayCurvesBody(company, kpi, runId, rawDecayKey, models, slots);
      return Object.keys(body.models).length === 0
        ? Promise.resolve({ decayData: [], decayStructure: null })
        : fetchDecayCurvesFromLambda(body, slots, featureMap);
    },
    enabled: enabledCount > 0 && rawDecayKey != null,
    placeholderData: keepPreviousData
  });
};
